import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { Woordmerk } from '../components/ui';
import { Symbool } from '../components/icons';
import { useOpening, useScrambleBijScroll } from '../lib/animatie';

/* ==========================================================================
   Landingspagina — het enige scherm dat je zonder account te zien krijgt.
   Vertelt wat de app doet en leidt naar aanmelden of inloggen.

   De opbouw is een trap: eerst de foto met de belofte eroverheen, dan wat het
   is, dan wat het voor je doet (donker, zodat de pagina halverwege ademt), dan
   hoe het werkt, en tot slot de vraag om je aan te melden.
   ========================================================================== */

const KENMERKEN = [
  {
    ikoon: 'stijging',
    titel: 'Elo-rating, geen streepjeslijst',
    tekst:
      'Winnen van de nummer één telt zwaarder dan winnen van de stagiair. Iedereen begint op 800 en de ranglijst regelt zichzelf.'
  },
  {
    ikoon: 'kalender',
    titel: 'Elk kwartaal een nieuw seizoen',
    tekst:
      'Met een zachte reset: een kwart van je voorsprong gaat mee. Wie een goed kwartaal had begint iets hoger, maar niemand is bij voorbaat kansloos.'
  },
  {
    ikoon: 'beker',
    titel: 'Toernooi in twee klikken',
    tekst:
      'Vink de deelnemers aan en het schema staat er, geplaatst op rating en aangevuld met byes. De sterkste treft de zwakste.'
  },
  {
    ikoon: 'statistiek',
    titel: 'Cijfers die iets zeggen',
    tekst:
      'Wie is jouw boeman, met wie win je het vaakst, en op welke middag wordt er het hardst gespeeld. Inclusief onderlinge balans.'
  }
];

const STAPPEN = [
  { nr: 1, titel: 'Speel je potje', tekst: 'Gewoon spelen. Niemand hoeft iets bij te houden tijdens het spel.' },
  { nr: 2, titel: 'Vul de eindstand in', tekst: 'Achteraf, met datum en tijd. Vergeten? Terugdateren mag gewoon.' },
  { nr: 3, titel: 'De stand klopt weer', tekst: 'De stand wordt opnieuw doorgerekend, dus de volgorde blijft eerlijk.' }
];

export default function Landing() {
  const belofte = useRef<HTMLHeadingElement>(null);
  const tweedeKop = useRef<HTMLHeadingElement>(null);

  useOpening(belofte);
  useScrambleBijScroll(tweedeKop);

  return (
    <div className="min-h-screen bg-krijt-50">
      {/* De balk blijft staan bij het scrollen: de twee knoppen zijn het hele
          doel van deze pagina, dus die moeten altijd binnen bereik zijn. */}
      <header className="sticky top-0 z-40 border-b border-krijt-200 bg-krijt-50/85 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center gap-4 px-5 py-3.5">
          <Woordmerk toon="licht" />

          <nav className="ml-auto flex items-center gap-1 sm:gap-2">
            <a href="#watje" className="hidden px-3 py-2 text-sm font-medium text-krijt-600 hover:text-krijt-900 sm:block">
              Wat het doet
            </a>
            <a href="#hoe" className="hidden px-3 py-2 text-sm font-medium text-krijt-600 hover:text-krijt-900 sm:block">
              Hoe het werkt
            </a>
            <Link
              to="/inloggen"
              className="rounded-md border border-krijt-300 bg-white px-3.5 py-2 text-sm font-medium transition-all duration-150 hover:-translate-y-px hover:bg-krijt-100 hover:shadow-[0_2px_8px_rgba(11,11,11,0.10)]"
            >
              Inloggen
            </Link>
            <Link
              to="/aanmelden"
              className="rounded-md border border-veld-700 bg-veld-700 px-3.5 py-2 text-sm font-medium text-white transition-all duration-150 hover:-translate-y-px hover:bg-veld-800 hover:shadow-[0_2px_8px_rgba(11,11,11,0.16)]"
            >
              Account aanmaken
            </Link>
          </nav>
        </div>
      </header>

      {/* Paginabreed, en hoog genoeg om iets van de tafel te laten zien.
          De foto is decor: geen tekst erin, dus geen alt. */}
      <div className="relative">
        <img
          src="/foto/landing.jpg"
          alt=""
          aria-hidden="true"
          className="h-[46vh] min-h-[280px] w-full object-cover sm:h-[58vh]"
        />
        <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-krijt-950/90 via-krijt-950/45 to-krijt-950/10" />

        <div className="absolute inset-x-0 bottom-0">
          <div className="mx-auto max-w-6xl px-5 pb-10 sm:pb-14">
            <span className="inline-flex items-center rounded-full border border-white/25 bg-white/10 px-3 py-1 text-xs font-semibold text-white backdrop-blur">
              Voor de tafel op kantoor
            </span>
            {/*
              De opening: woord voor woord, met splitText.

              De regelhoogte is bewust 1.2 en niet strakker. splitText zet elk
              woord in een doosje met `overflow: clip` — precies zo hoog als de
              regel — en daar viel de staart van de j en de g buiten: Google Sans
              vraagt 1,25 keer de lettergrootte aan hoogte, dus bij 1.05 werd er
              onderaan een paar pixels afgesneden. Vanaf 1.14 past het; 1.2 geeft
              wat lucht.
            */}
            <h1
              ref={belofte}
              className="mt-4 max-w-2xl font-display text-[38px] font-bold leading-[1.2] tracking-[-0.02em] text-white sm:text-[56px]"
            >
              Elf poppetjes, één ranglijst.
            </h1>
            <p className="mt-3 max-w-lg text-[15px] leading-relaxed text-white/80 sm:text-[17px]">
              Foosrankr rekent elk potje door met een Elo-rating en maakt van een middagje tafelvoetbal een competitie
              die klopt.
            </p>
          </div>
        </div>
      </div>

      <section className="mx-auto grid max-w-6xl items-center gap-10 px-5 py-14 lg:grid-cols-2 lg:py-20">
        <div>
          <h2 className="text-[30px] font-bold leading-tight tracking-[-0.02em] sm:text-[36px]">
            De tafel houdt de stand bij.
          </h2>
          <p className="lopend mt-4 max-w-md">
            Wie is er nou écht de beste? Iedereen begint op 800. Winnen van iemand die hoger staat levert meer op dan
            winnen van iemand die lager staat, en elk kwartaal begint een nieuw seizoen met een zachte reset.
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link
              to="/aanmelden"
              className="rounded-md border border-veld-700 bg-veld-700 px-5 py-2.5 font-medium text-white transition-all duration-150 hover:-translate-y-px hover:bg-veld-800 hover:shadow-[0_2px_10px_rgba(11,11,11,0.16)]"
            >
              Account aanmaken
            </Link>
            <Link to="/inloggen" className="px-2 py-2.5 font-medium text-veld-800 hover:underline">
              Ik heb al een account →
            </Link>
          </div>
          <p className="mt-3 text-xs text-krijt-500">
            Nieuwe accounts worden eerst goedgekeurd door een beheerder, zodat de competitie van jullie blijft.
          </p>
        </div>

        {/* Geen screenshot maar de echte opmaak: dit is precies hoe de ranglijst eruitziet. */}
        <div className="rounded-kaart border border-krijt-200 bg-white p-4 shadow-[0_8px_30px_rgba(11,11,11,0.06)]">
          <div className="mb-3 flex items-baseline justify-between">
            <h3 className="t3">Ranglijst</h3>
            <span className="text-xs text-krijt-500">3e kwartaal</span>
          </div>
          <table className="w-full border-collapse text-sm">
            <tbody>
              {[
                { plek: 1, naam: '@imre', rating: 1312, vorm: [1, 1, 0, 1, 1] },
                { plek: 2, naam: '@sanne', rating: 1243, vorm: [1, 0, 1, 1, 0] },
                { plek: 3, naam: '@nadia', rating: 1201, vorm: [0, 1, 1, 0, 1] },
                { plek: 4, naam: '@feline', rating: 1194, vorm: [1, 0, 0, 1, 0] },
                { plek: 5, naam: '@bram', rating: 1125, vorm: [0, 0, 1, 0, 0] }
              ].map((r) => (
                <tr key={r.naam} className="border-b border-krijt-200 last:border-b-0">
                  <td className="py-2 pr-2">
                    <span
                      className={`nums font-semibold ${
                        r.plek === 1 ? 'text-goud' : r.plek === 2 ? 'text-krijt-500' : r.plek === 3 ? 'text-amber-800' : 'text-krijt-400'
                      }`}
                    >
                      {r.plek}
                    </span>
                  </td>
                  <td className="py-2">
                    <span className="flex items-center gap-2">
                      <span className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-black/10 bg-krijt-100 text-[11px] font-semibold text-krijt-600">
                        {r.naam.slice(1, 3).toUpperCase()}
                      </span>
                      {r.naam}
                    </span>
                  </td>
                  <td className="nums py-2 text-right font-semibold">{r.rating}</td>
                  <td className="py-2 pl-3">
                    <span className="flex justify-end gap-[3px]">
                      {r.vorm.map((g, i) => (
                        <span
                          key={i}
                          className={`inline-flex h-4 w-4 items-center justify-center rounded-sm text-[9px] font-bold text-white ${
                            g ? 'bg-goed' : 'bg-kritiek'
                          }`}
                        >
                          {g ? 'W' : 'V'}
                        </span>
                      ))}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/*
        De donkere sectie halverwege: hij breekt de pagina en zet de vier
        kenmerken apart. Wit op speelveldgroen, iconen in het lichte groen van
        het thema zodat ze niet met de tekst concurreren.
      */}
      <section id="watje" className="bg-veld-900 py-16 text-white">
        <div className="mx-auto max-w-6xl px-5">
          <h2
            ref={tweedeKop}
            className="font-display text-[30px] font-bold uppercase tracking-[-0.01em] sm:text-[38px]"
          >
            Wat het voor je doet
          </h2>
          <p className="mt-3 max-w-xl text-[15px] leading-relaxed text-veld-100">
            Geen gedoe met een whiteboard dat niemand bijhoudt. Vier dingen die het verschil maken.
          </p>

          <div className="mt-10 grid gap-x-10 gap-y-9 sm:grid-cols-2">
            {KENMERKEN.map((k) => (
              <div key={k.titel} className="flex gap-4">
                <span className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/10 ring-1 ring-white/15">
                  <Symbool naam={k.ikoon} className="!h-6 !w-6 text-veld-200" />
                </span>
                <div>
                  <h3 className="t3 text-white">{k.titel}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-veld-100">{k.tekst}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="hoe" className="mx-auto max-w-6xl px-5 py-16">
        <h2 className="text-[26px] font-bold tracking-[-0.02em] sm:text-[30px]">Zo werkt het</h2>
        <div className="mt-8 grid gap-7 sm:grid-cols-3">
          {STAPPEN.map((s) => (
            <div key={s.nr}>
              <span className="nums inline-flex h-9 w-9 items-center justify-center rounded-full bg-veld-700 text-sm font-semibold text-white">
                {s.nr}
              </span>
              <h3 className="t3 mt-3">{s.titel}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-krijt-600">{s.tekst}</p>
            </div>
          ))}
        </div>

        <div className="mt-14 flex flex-wrap items-center justify-between gap-4 rounded-kaart border border-veld-200 bg-veld-50 px-6 py-6">
          <div>
            <h3 className="t2">Klaar om de eerste stand te zetten?</h3>
            <p className="mt-1 text-sm text-krijt-600">Aanmelden duurt een halve minuut.</p>
          </div>
          <Link
            to="/aanmelden"
            className="rounded-md border border-veld-700 bg-veld-700 px-5 py-2.5 font-medium text-white transition-all duration-150 hover:-translate-y-px hover:bg-veld-800 hover:shadow-[0_2px_10px_rgba(11,11,11,0.16)]"
          >
            Account aanmaken
          </Link>
        </div>
      </section>

      <footer className="border-t border-krijt-200 py-7 text-center text-xs text-krijt-500">
        Foosrankr · gebouwd voor één tafel, één kantoor
      </footer>
    </div>
  );
}
